import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  CircularProgress,
  Divider,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { getClients, getUserList, grantPermission, revokePermission } from "../../api";
import { useDispatch, useSelector } from "react-redux";
import { showSnackbar } from "../../store/snackBarSlice";
import { hideLoader, showLoader } from "../../store/loaderSlice";
import { RootState } from "../../store/appStore";

interface ShareModalProps {
  open: boolean;
  onClose: () => void;
  contractId: string;
  contractName: string;
  contractIndexId?: number;
}

interface User {
  user_id: number;
  email: string;
  first_name: string;
  last_name: string;
  client_name?: string;
  is_shared?: boolean;
}

const ShareModal: React.FC<ShareModalProps> = ({
  open,
  onClose,
  contractId,
  contractName,
  contractIndexId,
}) => {
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<number | "">("");
  const [loading, setLoading] = useState<boolean>(false);
  const dispatch = useDispatch();
  const currentUser: any = useSelector((state: RootState) => state.auth.user);

  useEffect(() => {
    if (open) {
      setSelectedUser("");
      fetchUsers();
    }
  }, [open, contractIndexId]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const pageSize = 100;
      let currentPage = 1;
      let totalPages = 1;
      const allUsers: User[] = [];
      let targetClientName: string | undefined;

      while (currentPage <= totalPages) {
        const response = await getUserList("", currentPage, pageSize);
        if (!response?.success || !response?.data) {
          throw new Error(response?.payload || "Failed to load users");
        }

        allUsers.push(...response.data);
        totalPages = response?.pagination?.total_pages || 1;
        currentPage += 1;
      }

      if (contractIndexId !== undefined) {
        let clientPage = 1;
        let clientTotalPages = 1;

        while (clientPage <= clientTotalPages && !targetClientName) {
          const clientResponse = await getClients("", clientPage, pageSize);
          if (!clientResponse?.success || !clientResponse?.data) {
            throw new Error(clientResponse?.payload || "Failed to load clients");
          }

          const matchedClient = clientResponse.data.find(
            (client: { client_id: number; client_name: string }) =>
              String(client.client_id) === String(contractIndexId)
          );

          if (matchedClient) {
            targetClientName = matchedClient.client_name;
          }

          clientTotalPages = clientResponse?.pagination?.total_pages || 1;
          clientPage += 1;
        }
      }

      if (!targetClientName && currentUser?.client_name) {
        targetClientName = currentUser.client_name;
      }

      if (targetClientName) {
        setUsers(
          allUsers.filter(
            (user) => user.client_name === targetClientName
          )
        );
      } else {
        setUsers(allUsers);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      dispatch(
        showSnackbar({
          message: "Failed to load users",
          severity: "error",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const handleShare = async () => {
    if (selectedUser === "") {
      dispatch(
        showSnackbar({
          message: "Please select a user to share with",
          severity: "warning",
        })
      );
      return;
    }

    dispatch(showLoader());
    try {
      await grantPermission(selectedUser as number, parseInt(contractId));

      dispatch(
        showSnackbar({
          message: "Contract shared successfully",
          severity: "success",
        })
      );
      onClose();
    } catch (error) {
      console.error("Error sharing contract:", error);
      dispatch(
        showSnackbar({
          message: "Failed to share contract",
          severity: "error",
        })
      );
    } finally {
      dispatch(hideLoader());
    }
  };

  const handleUserChange = (event: SelectChangeEvent<number | "">) => {
    setSelectedUser(event.target.value as number | "");
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Share Contract</Typography>
          <IconButton edge="end" color="inherit" onClick={onClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Box mb={2}>
          <Typography variant="body1" gutterBottom>
            Share <strong>{contractName}</strong> with another user
          </Typography>
        </Box>
        <Box sx={{ minHeight: 120 }}>
          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress size={30} />
            </Box>
          ) : users && users.length > 0 ? (
            <FormControl fullWidth variant="outlined">
              <InputLabel id="user-select-label">Select User</InputLabel>
              <Select
                labelId="user-select-label"
                id="user-select"
                value={selectedUser}
                onChange={handleUserChange}
                label="Select User"
              >
                <MenuItem value="">
                  <em>Select a user</em>
                </MenuItem>
                {users.map((user) => (
                  <MenuItem key={user.user_id} value={user.user_id}>
                    {`${user.first_name} ${user.last_name}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <Typography variant="body2" color="textSecondary" align="center">
              No same-index users found
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary">
          Cancel
        </Button>
        <Button
          onClick={handleShare}
          variant="contained"
          color="primary"
          disabled={selectedUser === ""}
        >
          Share
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShareModal;
